import type {
  SeriesRun,
  SeriesUnit,
  SeriesRunVisit,
  SeriesUpdateBody,
  SeriesUpdateVisit,
} from 'features/Series/api/series.types'
import { MAX_SERIES_VISITS } from 'features/Series/series.constants'

import type { GridRow, SeriesFooter, SeriesRowNotes } from '../series.types'
import { addUnitClamped, apptStatusLabel, daysBetween, toUsDate, unitLabel } from '../seriesDates'
import { samePeople, unknownPeopleOf, visitPeopleOf, type VisitPeople } from '../seriesPeople'
import { SERIES_TEXT_V2 } from '../seriesText'

export type SeriesVisitDraft = {
  date: string
  time: string
  duration: number
  people: VisitPeople | null
  // OpenEMR apptstat code.
  status: string | null
}

export type SeriesRunDraft = {
  name: string
  note: string
  issueIds: number[]
  applyIssuesToRemaining: boolean
  visits: Record<number, SeriesVisitDraft>
  extendBy: number
  cancelled: boolean
  shift: { unit: SeriesUnit; n: number }
}

const HH_MM = /^\d{2}:\d{2}/

// The API may answer HH:mm:ss; the drawer edits and sends HH:mm.
const toHourMinute = (time: string): string => (HH_MM.test(time) ? time.slice(0, 5) : time)

export { toUsDate }

export const isBookedVisit = (
  visit: SeriesRunVisit,
): visit is SeriesRunVisit & { date: string; time: string } =>
  visit.bucket !== 'unbooked' && visit.date !== null && visit.time !== null

export const maxExtend = (run: SeriesRun): number => Math.max(0, MAX_SERIES_VISITS - run.total)

export const draftFromRun = (run: SeriesRun): SeriesRunDraft => ({
  name: run.name,
  note: run.note,
  issueIds: run.issues.map((issue) => issue.id),
  applyIssuesToRemaining: false,
  visits: Object.fromEntries(
    run.visits.filter(isBookedVisit).map((visit) => [
      visit.step,
      {
        date: visit.date,
        time: toHourMinute(visit.time),
        duration: visit.duration,
        people: visitPeopleOf(visit),
        status: visit.status,
      },
    ]),
  ),
  extendBy: 0,
  cancelled: run.status === 'cancelled',
  shift: { unit: 'week', n: 0 },
})

const sameIds = (left: number[], right: number[]): boolean => {
  const a = [...new Set(left)].sort((x, y) => x - y)
  const b = [...new Set(right)].sort((x, y) => x - y)
  return a.length === b.length && a.every((id, index) => id === b[index])
}

// A visit naming someone without a Residen user never sends people: the list would drop them.
const hasUnknownPerson = (visit: SeriesRunVisit | undefined): boolean =>
  !!visit && unknownPeopleOf(visit).names.length > 0

// The people to send for a visit, or null when they stay as saved.
const sentPeople = (
  visit: SeriesRunVisit | undefined,
  saved: SeriesVisitDraft,
  draft: SeriesVisitDraft,
): VisitPeople | null =>
  draft.people !== null && !hasUnknownPerson(visit) && !samePeople(draft.people, saved.people)
    ? draft.people
    : null

const changedVisit = (
  visit: SeriesRunVisit | undefined,
  step: number,
  saved: SeriesVisitDraft,
  draft: SeriesVisitDraft | undefined,
): SeriesUpdateVisit | null => {
  if (!draft) return null
  const durationChanged = draft.duration !== saved.duration
  const people = sentPeople(visit, saved, draft)
  const statusChanged = draft.status !== null && draft.status !== saved.status
  const moved = draft.date !== saved.date || draft.time !== saved.time
  if (!moved && !durationChanged && !people && !statusChanged) return null
  return {
    step,
    date: draft.date,
    time: draft.time,
    ...(durationChanged ? { duration: draft.duration } : {}),
    ...(people ? { doctor_ids: [...people.people], primary_doctor_id: people.primaryId } : {}),
    ...(statusChanged && draft.status !== null ? { status: draft.status } : {}),
  }
}

const runVisitAt = (run: SeriesRun, step: number): SeriesRunVisit | undefined =>
  run.visits.find((visit) => visit.step === step)

const changedVisits = (
  run: SeriesRun,
  saved: SeriesRunDraft,
  draft: SeriesRunDraft,
): SeriesUpdateVisit[] =>
  Object.entries(saved.visits)
    .map(([key, visit]) => {
      const step = Number(key)
      return changedVisit(runVisitAt(run, step), step, visit, draft.visits[step])
    })
    .filter((row): row is SeriesUpdateVisit => row !== null)
    .sort((a, b) => a.step - b.step)

export type PersonRemoval = { id: number; name: string; visits: number[] }

// Known people on a saved list whom the draft takes off, one entry per person.
export const removedPeople = (run: SeriesRun, draft: SeriesRunDraft): PersonRemoval[] => {
  const saved = draftFromRun(run)
  const taken = [...run.visits]
    .sort((a, b) => a.step - b.step)
    .flatMap((visit) => {
      const was = saved.visits[visit.step]
      const now = draft.visits[visit.step]
      const sent = was && now ? sentPeople(visit, was, now) : null
      if (!sent) return []
      return visit.people.flatMap(({ doctor }) =>
        doctor.id === null || sent.people.includes(doctor.id)
          ? []
          : [
              {
                id: doctor.id,
                name: doctor.name ?? SERIES_TEXT_V2.unknownProvider,
                step: visit.step,
              },
            ],
      )
    })
  return taken.reduce<PersonRemoval[]>((acc, { id, name, step }) => {
    const found = acc.find((item) => item.id === id)
    if (!found) return [...acc, { id, name, visits: [step] }]
    return acc.map((item) =>
      item.id === id && !item.visits.includes(step)
        ? { ...item, visits: [...item.visits, step] }
        : item,
    )
  }, [])
}

// Only what differs from the saved run; null when there is nothing to send.
export const buildUpdateBody = (run: SeriesRun, draft: SeriesRunDraft): SeriesUpdateBody | null => {
  const saved = draftFromRun(run)
  const name = draft.name.trim()
  const issuesChanged = !sameIds(saved.issueIds, draft.issueIds)
  // Apply with no problems would strip every remaining visit, so it is never sent alone.
  const applyIssues = draft.applyIssuesToRemaining && draft.issueIds.length > 0
  const sendIssues = issuesChanged || applyIssues
  const visits = changedVisits(run, saved, draft)
  const extendBy = Math.min(Math.max(0, Math.floor(draft.extendBy)), maxExtend(run))
  const body: SeriesUpdateBody = {
    ...(name && name !== saved.name ? { name } : {}),
    ...(draft.note !== saved.note ? { note: draft.note } : {}),
    ...(sendIssues ? { issue_ids: [...draft.issueIds] } : {}),
    ...(applyIssues ? { apply_issues_to_remaining: true } : {}),
    ...(visits.length ? { visits } : {}),
    ...(extendBy > 0 ? { extend_by: extendBy } : {}),
    ...(draft.cancelled !== saved.cancelled ? { cancelled: draft.cancelled } : {}),
  }
  return Object.keys(body).length ? body : null
}

// Moves the opened visit and every later booked one by one unit, whatever its status.
export const shiftOnward = (
  run: SeriesRun,
  draft: SeriesRunDraft,
  openedStep: number,
  dir: 1 | -1,
): SeriesRunDraft => ({
  ...draft,
  visits: Object.fromEntries(
    Object.entries(draft.visits).map(([key, visit]) => [
      key,
      Number(key) >= openedStep
        ? { ...visit, date: addUnitClamped(visit.date, draft.shift.unit, dir) }
        : visit,
    ]),
  ),
  shift: { ...draft.shift, n: draft.shift.n + dir },
})

export const setShiftUnit = (draft: SeriesRunDraft, unit: SeriesUnit): SeriesRunDraft => ({
  ...draft,
  shift: { unit, n: 0 },
})

// Whole days from the previous booked row; unbooked rows show nothing.
export const editIntervals = (run: SeriesRun, draft: SeriesRunDraft): Record<number, string> => {
  let previous: string | null = null
  return Object.fromEntries(
    run.visits.map((visit, index) => {
      const date = draft.visits[visit.step]?.date ?? null
      let label = ''
      if (index === 0) label = SERIES_TEXT_V2.intervalStart
      else if (date && previous) label = unitLabel(daysBetween(previous, date), 'day')
      if (date) previous = date
      return [visit.step, label]
    }),
  )
}

const rowName = (run: SeriesRun, draft: SeriesRunDraft): string =>
  draft.name.trim() || run.name || SERIES_TEXT_V2.noName

const noteTexts = (step: number, notes: SeriesRowNotes): string[] => [
  ...(notes.locked.includes(step) ? [SERIES_TEXT_V2.lockedVisit] : []),
  ...notes.warnings.filter((warning) => warning.step === step).map((warning) => warning.message),
  ...(notes.errors[step] ?? []),
]

const unbookedRow = (
  visit: SeriesRunVisit,
  base: Pick<GridRow, 'key' | 'visit' | 'name' | 'interval' | 'opened' | 'warnings' | 'refused'>,
): GridRow => ({
  ...base,
  date: visit.recallDate,
  time: null,
  duration: null,
  people: visitPeopleOf(visit),
  unknownPeople: unknownPeopleOf(visit),
  peopleReadOnly: true,
  doctorText: visit.recallDate ? SERIES_TEXT_V2.unbookedProvider : SERIES_TEXT_V2.unbookedNoRecall,
  doctorTone: visit.recallDate ? 'normal' : 'refused',
  status: SERIES_TEXT_V2.statusRecall,
  editable: false,
  edited: { date: false, time: false },
})

export const editGridRows = (
  run: SeriesRun,
  draft: SeriesRunDraft,
  openedStep: number,
  notes: SeriesRowNotes,
  openedDoctorId: number | null,
): GridRow[] => {
  const saved = draftFromRun(run)
  const intervals = editIntervals(run, draft)
  return run.visits.map((visit) => {
    const warnings = noteTexts(visit.step, notes)
    const base = {
      key: String(visit.step),
      visit: `${visit.step}/${run.total}`,
      name: rowName(run, draft),
      interval: intervals[visit.step] ?? '',
      opened: visit.step === openedStep,
      warnings,
      refused: (notes.errors[visit.step] ?? []).length > 0,
    }
    const current = draft.visits[visit.step]
    if (!current) return unbookedRow(visit, base)
    const was = saved.visits[visit.step]
    const unknownPeople = unknownPeopleOf(visit)
    return {
      ...base,
      date: current.date,
      time: current.time,
      duration: current.duration,
      people: current.people,
      unknownPeople,
      peopleReadOnly: unknownPeople.names.length > 0 || visit.bucket === 'completed',
      lockedPersonId: visit.step === openedStep ? openedDoctorId : null,
      status: apptStatusLabel(current.status),
      statusCode: current.status,
      savedStatusCode: was?.status ?? null,
      editable: visit.bucket !== 'completed',
      disabled: visit.bucket === 'completed',
      edited: { date: current.date !== was?.date, time: current.time !== was?.time },
    }
  })
}

export const runDateRange = (
  run: SeriesRun,
  draft: SeriesRunDraft,
): { first: string | null; last: string | null } => {
  const bookedDates = run.visits.flatMap((v) =>
    draft.visits[v.step] ? [draft.visits[v.step].date] : [],
  )
  const dates = bookedDates.length
    ? bookedDates
    : run.visits.flatMap((v) => (v.recallDate ? [v.recallDate] : []))
  const sorted = [...dates].sort()
  return { first: sorted[0] ?? null, last: sorted[sorted.length - 1] ?? null }
}

const attentionCount = (notes: SeriesRowNotes): number =>
  new Set([
    ...Object.entries(notes.errors)
      .filter(([, texts]) => texts.length > 0)
      .map(([step]) => Number(step)),
    ...notes.warnings.flatMap((warning) => (warning.step === null ? [] : [warning.step])),
    ...notes.locked,
  ]).size

export const editFooter = (
  run: SeriesRun,
  draft: SeriesRunDraft,
  openedStep: number,
  notes: SeriesRowNotes,
): SeriesFooter => {
  const { first, last } = runDateRange(run, draft)
  const head = `${rowName(run, draft)} · ${run.total} visits`
  const attention = attentionCount(notes)
  const saved = draftFromRun(run)
  const moved = Object.entries(draft.visits).some(
    ([step, visit]) => visit.date !== saved.visits[Number(step)]?.date,
  )
  const extend = Math.min(Math.max(0, Math.floor(draft.extendBy)), maxExtend(run))
  return {
    kicker: SERIES_TEXT_V2.kickerSeries,
    title: first && last ? `${head} · ${toUsDate(first)} – ${toUsDate(last)}` : head,
    subtitle:
      SERIES_TEXT_V2.footerOpened(openedStep) +
      (moved ? SERIES_TEXT_V2.footerMoved(openedStep, run.total) : '') +
      (extend > 0 ? SERIES_TEXT_V2.footerAdded(extend) : '') +
      (attention > 0 ? SERIES_TEXT_V2.footerAttention(attention) : ''),
    tone: attention > 0 ? 'warn' : 'muted',
    saveLabel: SERIES_TEXT_V2.save,
  }
}
